import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import Menu from '@base/Menu/Menu';
import MoreVert from '@icon/MoreVert';
import {
  setModalState,
  deletePracticeReminder,
  fetchPracticeReminderCardInfo
} from '@actions/practiceSession';
import {
  getIsReminderSettingsAvailable,
  getIsReminderSettingsActive,
  getIsReminderDisplayable
} from '@selectors/practiceSession';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  PRACTICE_SETTINGS_CLICKED,
  DPP_REMOVE_DPP_CLICKED,
  DPP_EDIT_REMINDER_TIME_CLICKED,
  DPP_SET_REMINDER_CLICKED
} from '@constants/segment';
import { Spacings } from '@styles/index';
import usePracticeSegmentAnalytics from '../../usePracticeSegmentAnalytics';
import { MODAL_TYPES } from '../../constants';

const MenuButton = styled.div`
  cursor: pointer;
  float: right;
`;

const MenuItem = styled.li`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
`;

const ReminderActions = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { goalUID } = router.query;

  const isReminderSettingActive = useSelectorWithShallowEqual(
    getIsReminderSettingsActive(goalUID)
  );
  const isReminderSettingsAvailable = useSelectorWithShallowEqual(
    getIsReminderSettingsAvailable(goalUID)
  );
  const isReminderBannerDisplayed = useSelectorWithShallowEqual(
    getIsReminderDisplayable(goalUID)
  );

  const [anchorEl, setAnchorEl] = useState(null);

  const segmentAnalytics = usePracticeSegmentAnalytics();

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const sendSettingsClickEvent = () => {
    segmentAnalytics(PRACTICE_SETTINGS_CLICKED);
  };

  const sendDeleteSettingsClickEvent = () => {
    segmentAnalytics(DPP_REMOVE_DPP_CLICKED);
  };

  const sendEditSettingsClickEvent = () => {
    segmentAnalytics(DPP_EDIT_REMINDER_TIME_CLICKED);
  };

  const sendSetReminderEvent = () => {
    segmentAnalytics(DPP_SET_REMINDER_CLICKED);
  };

  const onMenuClick = (e) => {
    setAnchorEl(e.currentTarget);

    sendSettingsClickEvent();
  };

  const handleOnAddReminder = () => {
    handleMenuClose();

    dispatch(
      setModalState({
        type: MODAL_TYPES.PRACTICE_REMINDER,
        data: {}
      })
    );

    sendSetReminderEvent();
  };

  const handleOnEditReminder = () => {
    handleMenuClose();

    dispatch(
      setModalState({
        type: MODAL_TYPES.PRACTICE_REMINDER,
        data: {}
      })
    );

    sendEditSettingsClickEvent();
  };

  const handleOnDeleteReminder = async () => {
    sendDeleteSettingsClickEvent();
    handleMenuClose();

    await dispatch(deletePracticeReminder(goalUID));
    await dispatch(fetchPracticeReminderCardInfo(goalUID));
  };

  const shouldHideAction =
    isReminderBannerDisplayed || !isReminderSettingsAvailable;

  if (shouldHideAction) {
    return null;
  }

  return (
    <>
      <MenuButton onClick={onMenuClick}>
        <MoreVert
          size={Spacings.SPACING_5B}
          color="var(--color-text-primary)"
        />
      </MenuButton>
      <Menu
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        position="left"
        width="140px"
      >
        {isReminderSettingActive ? (
          <>
            <MenuItem onClick={handleOnEditReminder}>
              <P1 color="var(--color-text-primary)">Edit reminder</P1>
            </MenuItem>

            <MenuItem onClick={handleOnDeleteReminder}>
              <P1 color="var(--color-i-red)">Delete reminder</P1>
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={handleOnAddReminder}>
            <P1 color="var(--color-text-primary)">Add reminder</P1>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ReminderActions;
